// The privacy page, set as the book's other front matter: a publisher's note,
// not boilerplate. It keeps the about page's printed idiom (Playfair masthead,
// Source Serif body, roman-numeral chapters over hairlines) because the
// disclosures read best where they are true — in the product's own type, in
// plain sentences a reader can actually check against the site's behaviour.
//
// Substance over ceremony: this page is the stated basis for the consent
// asserted on every ad-conversion upload (`convex/model/dataManagerEvent.ts`,
// `docs/ops/conversions.md` §Consent). Chapter III is the load-bearing one —
// it must keep saying, plainly, that reading behaviour is used for advertising
// measurement, and only for visitors who arrived from an ad. If the pipeline
// changes, this page changes in the same commit.

import { Link } from "react-router";

/** A chapter opening: numeral and title in small caps over a hairline —
 *  the same idiom as `about-page.tsx`. */
function ChapterHeading({
  numeral,
  title,
}: {
  numeral: string;
  title: string;
}) {
  return (
    <h2 className="text-muted-foreground pt-6 text-center font-serif text-[0.75rem] tracking-[0.35em] uppercase">
      <span className="font-rockwell">{numeral}</span>
      <span aria-hidden="true"> · </span>
      {title}
    </h2>
  );
}

export function PrivacyPage() {
  return (
    <div className="bg-background min-h-svh">
      <main className="mx-auto max-w-2xl px-6 pt-20 pb-16 sm:px-8">
        {/* Masthead — matches the library's title page. */}
        <header className="mb-12 text-center">
          <p className="text-muted-foreground font-serif text-sm tracking-[0.35em] uppercase">
            A publisher’s note
          </p>
          <h1 className="font-display text-foreground mt-3 text-5xl font-black tracking-tight sm:text-6xl">
            On Privacy
          </h1>
          <p className="text-muted-foreground mt-6 font-serif text-sm italic">
            Last revised August 17, 2026
          </p>
          <div
            aria-hidden="true"
            className="border-border mx-auto mt-10 w-24 border-t"
          />
        </header>

        <article className="text-foreground space-y-6 font-serif text-[1.05rem] leading-relaxed">
          <ChapterHeading numeral="I" title="What This Browser Keeps" />
          <p className="first-letter:font-display first-letter:float-left first-letter:mt-1 first-letter:pr-3 first-letter:text-[3.4rem] first-letter:leading-[0.8] first-letter:font-black">
            Retells keeps its bookkeeping in your own browser. Your type size,
            language choice, bookmarks, and the chapters you’ve marked read live
            in this browser’s storage and go nowhere else. Alongside them sit
            two random identifiers minted the first time you visit — a reader id
            and a visitor id — with no name, email, or account attached. The
            visitor id is also set as a first-party cookie for about ninety
            days, so that a browser which clears its own storage on a schedule
            (Safari does) doesn’t mistake a returning reader for a new one.
            Clear this site’s data and all of it is gone; you would simply be a
            new visitor next time.
          </p>

          <ChapterHeading numeral="II" title="Reading, Measured" />
          <p>
            The reader reports anonymous progress — which chapter is open and
            how far into it you’ve read — keyed to those random identifiers.
            This is how the shelf learns which retellings hold attention and
            where readers stop, and how problems on the site get found and
            fixed. These measurements ride to our own logging service; no
            third-party analytics or advertising script runs on these pages.
          </p>

          <ChapterHeading numeral="III" title="Arriving from an Ad" />
          <p>
            If the address that brought you here carried an ad-click tag —
            Google’s <code className="text-[0.9em]">gclid</code> and its kin —
            that tag is stored in this browser. From then on, reading milestones
            reached in this browser — finishing a first chapter, coming back for
            another, creating an account — any purchase, and a classroom inquiry
            sent from the teachers&rsquo; page are reported to Google Ads
            against that one click, each with an estimated value. In plain
            terms: for visitors who arrive from an ad,{" "}
            <em>reading behaviour is used for advertising measurement</em>, so
            that ads are bought for people who actually read rather than people
            who merely land. What is sent is the click tag, the milestone, and a
            number — never your name, your email, or a list of what you read.
            Arrive without an ad tag and nothing about you is ever reported to
            any advertising platform.
          </p>

          <ChapterHeading numeral="IV" title="Accounts and Purchases" />
          <p>
            An account is optional; the shelf is readable without one. If you
            make one, sign-in is handled by our authentication service, which
            holds your email so you can get back in. Payments are taken by the
            sara.ai checkout — card details never touch this site. Purchase
            records are kept, as they must be, for receipts, refunds, and the
            ordinary obligations of bookkeeping.
          </p>

          <ChapterHeading numeral="V" title="What Is Not Done" />
          <p>
            Retells does not sell personal information, does not follow you
            across other sites, and does not embed advertising or tracking
            scripts in its pages. Nothing is shared with anyone beyond what
            these chapters describe: the advertising measurement of chapter III,
            and the infrastructure — hosting, logging, sign-in, checkout — that
            runs the site.
          </p>

          <ChapterHeading numeral="VI" title="Your Choices" />
          <p>
            Read without an account and this site knows you only as its random
            identifiers. Clear the site’s data in your browser and it forgets
            even those. And for anything the browser can’t reach — questions,
            corrections, or the deletion of an account and its records — write
            to{" "}
            <a
              href="mailto:rf@sara.ai"
              className="decoration-foreground/40 hover:text-primary hover:decoration-primary underline decoration-dotted underline-offset-[0.25em] transition-colors"
            >
              rf@sara.ai
            </a>{" "}
            and a human will answer.
          </p>
        </article>

        <div className="mt-14 flex items-center justify-center gap-8">
          <Link
            to="/about"
            className="border-foreground text-foreground hover:border-primary hover:text-primary border-b pb-1 font-serif text-sm tracking-[0.2em] uppercase transition-colors"
          >
            About Retells
          </Link>
          <Link
            to="/"
            className="border-foreground text-foreground hover:border-primary hover:text-primary border-b pb-1 font-serif text-sm tracking-[0.2em] uppercase transition-colors"
          >
            Browse the shelf
          </Link>
        </div>
      </main>
    </div>
  );
}
